import { FC, useEffect, useState } from "react";
import { useWindowDimensions } from "react-native";
import {
  AngryIcon,
  CheckIcon,
  FrownIcon,
  LaughIcon,
  LucideIcon,
  MehIcon,
  SmileIcon,
} from "lucide-react-native";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import {
  VnDialog,
  VnDialogContent,
  VnDialogPortal,
} from "@/components/VnDialog";
import { VnIcon } from "@/components/VnIcon";
import { VnSheet, VnSheetContent } from "@/components/VnSheet";
import { VnText } from "@/components/VnText/VnText";
import { BaseTextInput } from "@/components/VnTextInput/BaseTextInput";
import { useSubmitCesSurveyResponseMutation } from "@/data/mutations/cesSurveysMutations";
import { CesSurveyTriggerPointType } from "@/domain/cesSurveys";
import { MOBILE_BREAKPOINT } from "@/domain/constants";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

// Scores at or below this are the ones worth asking a follow-up question about
const HARD_SCORE_THRESHOLD = 2;
const THANKS_DURATION_MS = 2000;

const SCORE_OPTIONS: {
  score: number;
  IconComponent: LucideIcon;
  labelI18nKey: I18nKeyType;
}[] = [
  {
    score: 1,
    IconComponent: AngryIcon,
    labelI18nKey: "native:cesSurvey.scoreVeryHard",
  },
  {
    score: 2,
    IconComponent: FrownIcon,
    labelI18nKey: "native:cesSurvey.scoreHard",
  },
  {
    score: 3,
    IconComponent: MehIcon,
    labelI18nKey: "native:cesSurvey.scoreOk",
  },
  {
    score: 4,
    IconComponent: SmileIcon,
    labelI18nKey: "native:cesSurvey.scoreEasy",
  },
  {
    score: 5,
    IconComponent: LaughIcon,
    labelI18nKey: "native:cesSurvey.scoreVeryEasy",
  },
];

const questionI18nKeyByTriggerPoint: Record<
  CesSurveyTriggerPointType,
  I18nKeyType
> = {
  CONSENT_FORM_SIGNED: "native:cesSurvey.questions.consentFormSigned",
  HOUSEHOLD_MEMBER_ADDED: "native:cesSurvey.questions.householdMemberAdded",
  INCOME_ADDED: "native:cesSurvey.questions.incomeAdded",
};

type CesSurveyPromptPropsType = {
  cesSurveyId: string | null;
  triggerPoint: CesSurveyTriggerPointType;
  onCompleted: () => void;
};

export const CesSurveyPrompt: FC<CesSurveyPromptPropsType> = ({
  cesSurveyId,
  triggerPoint,
  onCompleted,
}) => {
  const { isWeb } = usePlatformOS();
  const { width } = useWindowDimensions();
  const isDesktopWeb = isWeb && width >= MOBILE_BREAKPOINT;

  const [step, setStep] = useState<"score" | "feedback" | "thanks">("score");
  const [score, setScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");

  const { mutate: submitCesSurveyResponse } =
    useSubmitCesSurveyResponseMutation();

  useEffect(() => {
    setStep("score");
    setScore(null);
    setFeedback("");
  }, [cesSurveyId]);

  useEffect(() => {
    if (step !== "thanks") {
      return;
    }

    const timeout = setTimeout(onCompleted, THANKS_DURATION_MS);
    return () => clearTimeout(timeout);
  }, [step, onCompleted]);

  const submitAndThank = (
    submittedScore: number,
    submittedFeedback?: string,
  ) => {
    if (!cesSurveyId) {
      return;
    }

    submitCesSurveyResponse({
      id: cesSurveyId,
      cesSurveyData: {
        score: submittedScore,
        feedback: submittedFeedback,
      },
    });
    setStep("thanks");
  };

  const handleScorePress = (pressedScore: number) => {
    setScore(pressedScore);

    if (pressedScore <= HARD_SCORE_THRESHOLD) {
      setStep("feedback");
      return;
    }

    submitAndThank(pressedScore);
  };

  const handleContinuePress = () => {
    if (score === null) {
      return;
    }

    const trimmedFeedback = feedback.trim();
    submitAndThank(score, trimmedFeedback === "" ? undefined : trimmedFeedback);
  };

  /// Skipping, dismissing and closing all mean the same thing: no *response* is recorded
  // and the patient moves on
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      onCompleted();
    }
  };

  const content =
    step === "thanks" ? (
      <VnBox className="items-center gap-2 py-6">
        <VnBox className="bg-sidebar/10 mb-2 flex size-16 flex-shrink-0 items-center justify-center rounded-full">
          <VnIcon as={CheckIcon} className="text-sidebar" size={36} />
        </VnBox>
        <VnText variant="headerTwo" i18nKey="native:cesSurvey.thanksTitle" />
        <VnText
          variant="lgMedium"
          className="text-muted-foreground text-center"
          i18nKey="native:cesSurvey.thanksDescription"
        />
      </VnBox>
    ) : (
      <VnBox className="gap-5">
        <VnBox className="items-center gap-2">
          <VnText
            variant="mdBold"
            className="text-muted-foreground uppercase tracking-widest"
            i18nKey="native:cesSurvey.optionalLabel"
          />
          <VnText
            variant="headerTwo"
            className="text-center"
            i18nKey={questionI18nKeyByTriggerPoint[triggerPoint]}
          />
        </VnBox>

        <VnBox className="flex-row items-start gap-1">
          {SCORE_OPTIONS.map(
            ({ score: optionScore, IconComponent, labelI18nKey }) => (
              <VnButton
                key={optionScore}
                variant="ghost"
                className="web:hover:bg-transparent h-auto flex-1 flex-col gap-1.5 px-0 py-0"
                onPress={() => handleScorePress(optionScore)}>
                <VnBox
                  className={cn(
                    "items-center justify-center rounded-xl p-1.5",
                    score === optionScore
                      ? "bg-chart-one"
                      : "web:group-hover:bg-chart-one/40",
                  )}>
                  <VnIcon
                    as={IconComponent}
                    className="text-foreground size-10"
                  />
                </VnBox>
                <VnText
                  variant="sm"
                  numberOfLines={1}
                  className="text-muted-foreground text-center text-[0.8rem]"
                  i18nKey={labelI18nKey}
                />
              </VnButton>
            ),
          )}
        </VnBox>

        {step === "feedback" && (
          <VnBox className="gap-2">
            <VnText variant="mdBold" i18nKey="native:cesSurvey.feedbackLabel" />
            <VnBox className="flex-row">
              <BaseTextInput
                autoFocus
                value={feedback}
                onChangeText={setFeedback}
                i18nKey="native:cesSurvey.feedbackPlaceholder"
                className="h-12"
              />
            </VnBox>
          </VnBox>
        )}

        <VnBox className="flex-row items-center justify-center gap-3">
          <VnButton
            variant="ghost"
            className={cn(step === "feedback" && "flex-1")}
            i18nKey="common:skip"
            onPress={onCompleted}
            textClassName="text-muted-foreground"
          />
          {step === "feedback" && (
            <VnButton
              className="flex-[2]"
              i18nKey="common:continue"
              onPress={handleContinuePress}
            />
          )}
        </VnBox>
      </VnBox>
    );

  const isOpen = Boolean(cesSurveyId);

  return isDesktopWeb ? (
    <VnDialog open={isOpen} onOpenChange={handleOpenChange}>
      <VnDialogPortal>
        <VnDialogContent className="md:w-120 w-screen">
          {content}
        </VnDialogContent>
      </VnDialogPortal>
    </VnDialog>
  ) : (
    <VnSheet
      open={isOpen}
      onOpenChange={handleOpenChange}
      side="bottom"
      shouldShowCloseButton={false}
      contentClassName="overflow-hidden rounded-t-3xl">
      <VnSheetContent className="bg-background gap-4">
        <VnBox className="bg-border h-1 w-10 self-center rounded-full" />
        {content}
      </VnSheetContent>
    </VnSheet>
  );
};
